'use client';

import React, { useState } from 'react';
import { Info, CheckCircle, ChevronDown } from 'lucide-react';
import { useTheme } from '@/core/providers/ThemeProvider';

interface ComplianceGuidelinesProps {
  asDropdown?: boolean;
}

const ComplianceGuidelines: React.FC<ComplianceGuidelinesProps> = ({ asDropdown = false }) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  if (asDropdown) {
    return (
      <div className="border-t pt-4 mt-6" style={{ borderColor: theme.border }}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between py-2 hover:opacity-80 transition-opacity"
        >
          <div className="flex items-center">
            <Info className="h-4 w-4 mr-2" style={{ color: theme.textSecondary }} />
            <h3 className="text-sm font-medium" style={{ color: theme.textPrimary }}>Compliance Guidelines</h3>
          </div>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            style={{ color: theme.textSecondary }}
          />
        </button>

        {isExpanded && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Obtain Consent</div>
                <div className="text-xs" style={{ color: theme.textSecondary }}>Get explicit consent first</div>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Provide Opt-out</div>
                <div className="text-xs" style={{ color: theme.textSecondary }}>Include STOP instructions</div>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Business Hours</div>
                <div className="text-xs" style={{ color: theme.textSecondary }}>8 AM - 9 PM local time</div>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Keep Records</div>
                <div className="text-xs" style={{ color: theme.textSecondary }}>Document all consent</div>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-lg flex items-center" style={{
              backgroundColor: `${theme.info}08`,
              borderLeft: `3px solid ${theme.info}`
            }}>
              <Info className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: theme.info }} />
              <div>
                <span className="text-xs" style={{ color: theme.textSecondary }}>
                  Questions? Contact us at{' '}
                </span>
                <a
                  href="mailto:support@voley.us"
                  className="text-xs underline hover:opacity-80 transition-opacity"
                  style={{ color: theme.info }}
                >
                  support@voley.us
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-card-background rounded-lg border p-6" style={{ borderColor: theme.border }}>
      <div className="flex items-center mb-4">
        <Info className="h-5 w-5 mr-2" style={{ color: theme.textSecondary }} />
        <h2 className="text-lg font-medium" style={{ color: theme.textPrimary }}>Compliance Guidelines</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-start p-3 rounded-lg hover:bg-hover-background transition-colors">
          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Obtain Consent</div>
            <div className="text-xs" style={{ color: theme.textSecondary }}>Get explicit consent first</div>
          </div>
        </div>

        <div className="flex items-start p-3 rounded-lg hover:bg-hover-background transition-colors">
          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Provide Opt-out</div>
            <div className="text-xs" style={{ color: theme.textSecondary }}>Include STOP instructions</div>
          </div>
        </div>

        <div className="flex items-start p-3 rounded-lg hover:bg-hover-background transition-colors">
          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Business Hours</div>
            <div className="text-xs" style={{ color: theme.textSecondary }}>8 AM - 9 PM local time</div>
          </div>
        </div>

        <div className="flex items-start p-3 rounded-lg hover:bg-hover-background transition-colors">
          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium" style={{ color: theme.textPrimary }}>Keep Records</div>
            <div className="text-xs" style={{ color: theme.textSecondary }}>Document all consent</div>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 rounded-lg flex items-center justify-between" style={{
        backgroundColor: `${theme.info}08`,
        borderLeft: `3px solid ${theme.info}`
      }}>
        <div className="flex items-center">
          <Info className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: theme.info }} />
          <div>
            <span className="text-xs" style={{ color: theme.textSecondary }}>
              Questions about compliance? Contact us at{' '}
            </span>
            <a
              href="mailto:support@voley.us"
              className="text-xs underline hover:opacity-80 transition-opacity"
              style={{ color: theme.info }}
            >
              support@voley.us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceGuidelines; 